/**
 * /donate/manage?token=… — donor self-serve subscription page.
 *
 * Donors hit this via a magic link in their welcome email. The token is
 * verified by /api/stripe/subscriptions/lookup and returns the current
 * subscription state. From here donors can: change amount, update card
 * (via Stripe-hosted Billing Portal), or cancel.
 *
 * No login required — possession of the magic-link IS the auth (same pattern
 * as the Board Portal). Token expires in 30 days; donors can request a new
 * one by submitting their email on /donate/manage with no token.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useContentApi } from "../context/ContentContext";
import { mailbox } from "../lib/mailboxes";
import { CreditCard, Calendar, DollarSign, Loader2, X, Heart } from "lucide-react";
import api from "../lib/api";
import SEO from "../components/SEO";

const ManageSubscription = () => {
    const [params] = useSearchParams();
    const token = params.get("token");
    const [sub, setSub] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const { c } = useContentApi();
    const accountsEmail = mailbox(c, "accounts");

    useEffect(() => {
        if (!token) { setLoading(false); return; }
        api.get(`/stripe/subscriptions/lookup?token=${encodeURIComponent(token)}`)
            .then((r) => setSub(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Invalid or expired link"))
            .finally(() => setLoading(false));
    }, [token]);

    if (!token) {
        return (
            <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 py-20">
                <SEO title="Manage donation" description="Manage your monthly donation" path="/donate/manage" />
                <div className="max-w-md text-center">
                    <h1 className="font-heading text-4xl text-[#1C252E] font-medium mb-4">Manage your donation</h1>
                    <p className="text-[#4A5568] mb-6">Open the magic link from your welcome email to manage your monthly donation. Lost it? Email {accountsEmail} and we&apos;ll re-send.</p>
                </div>
            </div>
        );
    }

    if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E53935]" /></div>;
    if (error) return (
        <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4 py-20" data-testid="manage-error">
            <div className="max-w-md text-center">
                <h1 className="font-heading text-4xl text-[#1C252E] font-medium mb-3">Link expired</h1>
                <p className="text-[#4A5568]">{error}. Please email {accountsEmail} for a fresh link.</p>
            </div>
        </div>
    );

    return <ManageView sub={sub} setSub={setSub} />;
};

const ManageView = ({ sub, setSub }) => {
    const [editingAmount, setEditingAmount] = useState(false);
    const [newAmount, setNewAmount] = useState(sub.amount_usd);
    const [busy, setBusy] = useState(false);

    const nextCharge = sub.next_charge_unix ? new Date(sub.next_charge_unix * 1000) : null;
    const status = sub.status || "active";
    const isCanceled = status === "canceled";

    const saveAmount = async () => {
        if (!newAmount || newAmount <= 0) { toast.error("Enter a valid amount"); return; }
        setBusy(true);
        try {
            await api.post(`/stripe/subscriptions/${sub.subscription_id}/change-amount`, { amount_usd: parseFloat(newAmount) });
            setSub({ ...sub, amount_usd: parseFloat(newAmount) });
            setEditingAmount(false);
            toast.success("Amount updated — next charge will reflect the new amount.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not update amount");
        } finally { setBusy(false); }
    };

    const updateCard = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/stripe/subscriptions/${sub.subscription_id}/update-card-session`);
            window.location.href = data.url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not open update-card page");
            setBusy(false);
        }
    };

    const cancelSub = async () => {
        if (!window.confirm("Cancel your monthly donation? You can re-subscribe anytime.")) return;
        setBusy(true);
        try {
            await api.post(`/stripe/subscriptions/${sub.subscription_id}/cancel`);
            setSub({ ...sub, status: "canceled" });
            toast.success("Subscription cancelled. Thank you for your generous support so far.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not cancel");
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen bg-[#FDFBF7] py-12 lg:py-16" data-testid="manage-subscription-page">
            <SEO title="Manage your donation" description="Self-serve management for your Reddog Foundation monthly donation" path="/donate/manage" />
            <div className="max-w-2xl mx-auto px-6">
                <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] mb-3 flex items-center gap-2">
                    <Heart className="w-3.5 h-3.5" strokeWidth={2} /> Monthly donation
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl text-[#1C252E] font-medium mb-2">Thank you, {sub.donor_name?.split(" ")[0] || "friend"}.</h1>
                <p className="text-base text-[#4A5568] mb-10">{sub.donor_email}</p>

                <div className="bg-white border border-black/5 rounded-3xl p-6 sm:p-8 space-y-6">
                    {/* Amount */}
                    <div className="flex items-start justify-between gap-3 flex-wrap pb-5 border-b border-black/5">
                        <div className="flex items-start gap-3 flex-1">
                            <DollarSign className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-1" strokeWidth={1.5} />
                            <div className="flex-1">
                                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Monthly amount</p>
                                {!editingAmount ? (
                                    <p className="text-3xl font-heading text-[#1C252E] font-medium" data-testid="manage-amount">
                                        ${parseFloat(sub.amount_usd).toFixed(2)}
                                        <span className="text-base text-[#4A5568] font-normal">/mo</span>
                                    </p>
                                ) : (
                                    <div className="flex items-center gap-2 mt-1">
                                        <span className="text-2xl text-[#4A5568]">$</span>
                                        <input
                                            type="number"
                                            step="1"
                                            min="1"
                                            value={newAmount}
                                            onChange={(e) => setNewAmount(e.target.value)}
                                            data-testid="manage-amount-input"
                                            className="w-32 text-2xl font-heading border-b-2 border-[#E53935] focus:outline-none bg-transparent py-1"
                                        />
                                        <span className="text-base text-[#4A5568]">/mo</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        {!editingAmount ? (
                            !isCanceled && <button onClick={() => setEditingAmount(true)} data-testid="manage-edit-amount-btn" className="text-sm text-[#E53935] hover:underline">Change</button>
                        ) : (
                            <div className="flex gap-2">
                                <button onClick={saveAmount} disabled={busy} data-testid="manage-save-amount-btn" className="px-3 py-1.5 rounded-full text-xs font-medium bg-[#E53935] text-white hover:bg-[#D32F2F] disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
                                <button onClick={() => { setEditingAmount(false); setNewAmount(sub.amount_usd); }} className="px-3 py-1.5 rounded-full text-xs font-medium border border-black/10 text-[#4A5568]">Cancel</button>
                            </div>
                        )}
                    </div>

                    {/* Next charge + card */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-5 border-b border-black/5">
                        <div className="flex items-start gap-3">
                            <Calendar className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-1" strokeWidth={1.5} />
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Next charge</p>
                                <p className="text-sm text-[#1C252E] font-medium" data-testid="manage-next-charge">
                                    {isCanceled ? "— (cancelled)" : (nextCharge ? nextCharge.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—")}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <CreditCard className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-1" strokeWidth={1.5} />
                            <div className="flex-1">
                                <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">Card on file</p>
                                <p className="text-sm text-[#1C252E] font-medium">{sub.card_last4 ? `•••• ${sub.card_last4}` : "—"}</p>
                                {!isCanceled && (
                                    <button onClick={updateCard} disabled={busy} data-testid="manage-update-card-btn" className="text-xs text-[#E53935] hover:underline mt-1">Update card</button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Status / cancel */}
                    {!isCanceled ? (
                        <div className="flex items-center justify-between pt-2">
                            <p className="text-xs text-[#4A5568]">Status: <span className="font-bold text-green-700 uppercase tracking-wider">{status}</span></p>
                            <button onClick={cancelSub} disabled={busy} data-testid="manage-cancel-btn" className="text-sm text-[#E53935] hover:underline inline-flex items-center gap-1.5">
                                <X className="w-4 h-4" /> Cancel donation
                            </button>
                        </div>
                    ) : (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                            <p className="text-sm text-amber-900">This subscription is <strong>cancelled</strong>. Thank you for your generous support. We&apos;d love to have you back anytime — restart at <a href="/donate" className="underline">/donate</a>.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManageSubscription;
